<script setup>
import { motion } from "motion-v";
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useChat } from "@/composables/useSidebar";
import { useDevice } from "@/composables/useDevice";
import { useNotifications } from "@/composables/useNotifications";
import { useSocket } from "@/composables/useSocket";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: '',
  },
});

const emits = defineEmits(["close"]);

const { unreadNotificationCount } = useChat();
const { isMobile } = useDevice();
const {
  notifications: apiNotifications,
  unreadCount,
  fetchNotifications,
  fetchUnreadCount,
  markAsRead: markApiAsRead,
  markAllAsRead: markAllApiAsRead
} = useNotifications();
const { on, off } = useSocket();

// notification-system 요소 참조
const notificationSystemRef = ref(null);

// 읽지 않은 메시지 표시 체크박스 상태
const showUnreadOnly = ref(false);

// 현재 테마 감지
const currentTheme = ref('dark');

// 테마 감지 함수
const getTheme = () => {
  if (process.client) {
    const theme = document.documentElement.getAttribute('data-theme');
    return theme === 'light' ? 'light' : 'dark';
  }
  return 'dark';
};

// 이미지 경로 반환 함수 (notice.svg인 경우 테마에 따라 변경)
const getImagePath = (originalPath) => {
  if (originalPath === '/images/notice.svg') {
    return currentTheme.value === 'light'
      ? '/images/notice_light.svg'
      : '/images/notice.svg';
  }
  return originalPath;
};

// 테마 변경 감지
let themeObserver = null;

const watchTheme = () => {
  if (process.client) {
    // 초기 테마 설정
    currentTheme.value = getTheme();

    themeObserver = new MutationObserver(() => {
      currentTheme.value = getTheme();
    });

    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  }
};

// 알림 항목들 (API에서 가져옴)
const notifications = computed(() => {
  return apiNotifications.value.map(n => ({
    id: n.id,
    isNew: n.is_new,
    thumb: getThumbImage(n.type, n.thumb_image),
    title: n.title,
    content: n.content || '',
    time: n.time,
    link: n.link_text ? { text: n.link_text, to: n.link_url || '#' } : null,
    hasStrongTitle: n.type !== 'notice'
  }));
});

// 알림 타입에 따른 썸네일 이미지
const getThumbImage = (type, customImage) => {
  if (customImage) return customImage;

  const thumbMap = {
    'deposit': '/images/add_funds.svg',
    'withdraw': '/images/usdt.svg',
    'notice': '/images/notice.svg',
    'jackpot': '/images/notice.svg',
    'info': '/images/notice.svg'
  };

  return thumbMap[type] || '/images/notice.svg';
};

// 필터링된 알림 목록
const filteredNotifications = computed(() => {
  if (showUnreadOnly.value) {
    return notifications.value.filter((item) => item.isNew);
  }
  return notifications.value;
});

// 읽지 않은 알림 개수 업데이트
const updateUnreadCount = () => {
  unreadNotificationCount.value = unreadCount.value;
};

// unreadCount 변경 감지
watch(
  unreadCount,
  () => {
    if (process.client) {
      updateUnreadCount();
    }
  }
);

// 초기 개수 설정 및 알림 로드
onMounted(async () => {
  watchTheme();

  if (process.client) {
    try {
      // 알림 목록 로드
      await fetchNotifications();
      // 읽지 않은 개수 로드
      await fetchUnreadCount();
    } catch (error) {
      // 조용히 실패 처리
    }

    // 실시간 알림 수신
    on('notification:new', async (data) => {
      // // console.log('[Notification] New notification:', data);
      try {
        // 알림 목록 갱신
        await fetchNotifications();
        await fetchUnreadCount();
      } catch (error) {
        // 조용히 실패 처리
      }
    });
  }
});

// 모두 읽음으로 표시
const markAllAsRead = async () => {
  const result = await markAllApiAsRead();
  if (result.success) {
    // // console.log('[Notification] All marked as read');
  }
};

const closeNotification = () => {
  emits("close");
};

// 외부 클릭 핸들러 (isMobile이거나 variant가 'sub'일 때만 동작)
const handleOutsideClick = (event) => {
  // 조건 확인: isMobile이거나 variant가 'sub'일 때만
  if (!isMobile.value && props.variant !== 'sub') return;

  // 알림이 열려있지 않으면 무시
  if (!props.isOpen) return;

  // Header 영역 클릭 시 제외
  const headerElement = event.target.closest('.header, .header__gnb, .header__mobile-gnb');
  if (headerElement) {
    return;
  }

  // notification-system 영역 내부 클릭은 무시
  if (notificationSystemRef.value) {
    const element = notificationSystemRef.value;
    // DOM 요소인지 확인하고 contains 메서드 사용
    if (element && typeof element.contains === 'function' && element.contains(event.target)) {
      return;
    }
  }

  // 외부 클릭이면 닫기
  closeNotification();
};

// isOpen이 변경될 때 이벤트 리스너 추가/제거
watch(
  () => props.isOpen,
  (newValue) => {
    if (process.client) {
      if (newValue && (isMobile.value || props.variant === 'sub')) {
        // 알림이 열리고 조건이 맞으면 이벤트 리스너 추가
        window.addEventListener('click', handleOutsideClick);
      } else {
        // 알림이 닫히면 이벤트 리스너 제거
        window.removeEventListener('click', handleOutsideClick);
      }
    }
  },
  { immediate: true }
);

// 알림 클릭 시 읽음 처리
const handleNotificationClick = async (notification) => {
  if (notification.isNew) {
    await markApiAsRead(notification.id);
  }
};

// 컴포넌트 언마운트 시 이벤트 리스너 제거
onBeforeUnmount(() => {
  if (process.client) {
    window.removeEventListener('click', handleOutsideClick);
    if (themeObserver) {
      themeObserver.disconnect();
    }
    // 소켓 이벤트 리스너 제거
    off('notification:new');
  }
});
</script>
<template>
  <motion.aside
    :class="['notification-system', { 'notification-system--sub': variant === 'sub', 'notification-system--open': isOpen }]"
    :initial="{ x: '100%' }"
    :animate="
      isOpen
        ? {
            x: 0,
          }
        : {
            x: '100%',
          }
    "
    :transition="{
      type: 'spring',
      stiffness: 320,
      damping: 25,
      mass: 0.8,
    }"
  >
    <div ref="notificationSystemRef" class="notification-system__inner">
      <div class="notification-system__header">
        <h2 class="notification-system__title">시스템 알림</h2>
        <button type="button" class="ico-close" @click="closeNotification">
          <span class="blind">닫기</span>
        </button>
      </div>
      <div class="notification-system__body">
        <div class="notification-system__list">
          <div v-for="notification in filteredNotifications" :key="notification.id" class="notification-system__item"
            :class="{ 'notification-system__item--new': notification.isNew }"
            @click="handleNotificationClick(notification)">
            <div class="notification-system__item-thumb">
              <img :src="getImagePath(notification.thumb)" alt="" />
            </div>
            <div class="notification-system__item-content">
              <p class="notification-system__item-title">
                <template v-if="notification.hasStrongTitle">
                  <strong>{{ notification.title }}</strong>
                  {{ notification.content }}
                </template>
                <template v-else>
                  {{ notification.title }}
                </template>
              </p>
              <span class="notification-system__item-time">{{
                notification.time
              }}</span>
              <div v-if="notification.link" class="notification-system__item-link">
                <NuxtLink :to="notification.link.to" class="btn-primary btn-sm">
                  {{ notification.link.text }}
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="notification-system__footer">
        <div class="switchset switchset--type1 switchset--sm">
          <span class="switchset__tit">읽지 않은 메세지 표시</span>
          <label class="switchset__label">
            <input class="switchset__input" type="checkbox" role="switch" v-model="showUnreadOnly">
            <span class="switchset__bg"></span>
            <span class="switchset__thumb"></span>
          </label>
        </div>
        <button type="button" class="notification-system-read--all ico-before ico-check-thin" @click="markAllAsRead">
          모두 읽음으로 표시
        </button>
      </div>
    </div>
  </motion.aside>
</template>
<style scoped></style>
